import { Fee, HourlyRate, HourlyRateRow, Retainer, LumpSum, Text, LumpSumRow, LumpSumEstimateRow } from '@/interfaces/fees';
import { Proposal } from '@/interfaces/types';
import { v4 as uuidv4 } from 'uuid';
import { isEqual } from 'lodash';
import { defaultFeeTextPl } from '../PostProposalGeneration/SecondTab/defaultFeeText';
import { defaultFeeTextEn } from '../PostProposalGeneration/SecondTab/defaultFeeText';

export const FEE_ACTIONS = {
    ADD_FEE: 'add_fee',
    REMOVE_FEE: 'remove_fee',
    UPDATE_FEE: 'update_fee',
    ADD_TABLE_ROW: 'add_table_row',
    REMOVE_TABLE_ROW: 'remove_table_row',
    UPDATE_TABLE_ROW: 'update_table_row',
    REORDER_FEES: 'reorder_fees',
    REORDER_TABLE_ROWS: 'reorder_table_rows',
} as const;

export const defaultHourlyRates: HourlyRateRow[] = [
    { "id": uuidv4(), "position": "Partner", "rate": 220 },
    { "id": uuidv4(), "position": "Counsel", "rate": 190 },
    { "id": uuidv4(), "position": "Senior Associate", "rate": 150 },
    { "id": uuidv4(), "position": "Associate", "rate": 130 },
    { "id": uuidv4(), "position": "Junior Associate", "rate": 120 },
    { "id": uuidv4(), "position": "Paralegal", "rate": 90 },
];

type AddFeeAction = {
    type: typeof FEE_ACTIONS.ADD_FEE;
    payload: Fee;
};

type RemoveFeeAction = {
    type: typeof FEE_ACTIONS.REMOVE_FEE;
    index: number;
};

type UpdateFeeAction = {
    type: typeof FEE_ACTIONS.UPDATE_FEE;
    payload: Fee;
    index: number;
};

type AddTableRowAction = {
    type: typeof FEE_ACTIONS.ADD_TABLE_ROW;
    payload: HourlyRateRow | LumpSumRow;
    index: number;
};

type RemoveTableRowAction = {
    type: typeof FEE_ACTIONS.REMOVE_TABLE_ROW;
    index: number;
    tableIndex: number;
};

type UpdateTableRowAction = {
    type: typeof FEE_ACTIONS.UPDATE_TABLE_ROW;
    payload: HourlyRateRow | LumpSumRow;
    index: number;
    tableIndex: number;
};

type ReorderFeesAction = {
    type: typeof FEE_ACTIONS.REORDER_FEES;
    payload: Fee[];
};

type ReorderTableRowsAction = {
    type: typeof FEE_ACTIONS.REORDER_TABLE_ROWS;
    index: number;
    payload: (LumpSumRow | LumpSumEstimateRow)[];
};

type FeeAction = AddFeeAction | RemoveFeeAction | UpdateFeeAction | AddTableRowAction | RemoveTableRowAction | UpdateTableRowAction | ReorderFeesAction | ReorderTableRowsAction;

export function handleFeeAction(state: Proposal, action: FeeAction): Proposal {
    switch (action.type) {
        case FEE_ACTIONS.ADD_FEE:
            // Some logic here to handle the default fee text more smoothly
            let newFees = []
            if (state.fees.length >= 2) {
                const lastTwoFees = state.fees.slice(state.fees.length - 2);
                if (isEqual(lastTwoFees, defaultFeeTextEn) || isEqual(lastTwoFees, defaultFeeTextPl)) {
                    newFees = [...state.fees.slice(0, state.fees.length - 2), action.payload, ...state.fees.slice(state.fees.length - 2)];
                } else {
                    newFees = [...state.fees, action.payload];
                }
            } else {
                newFees = [...state.fees, action.payload];
            }

            return {
                ...state,
                fees: newFees,
            };

        case FEE_ACTIONS.REMOVE_FEE:
            return {
                ...state,
                fees: state.fees.filter((_, index) => index !== action.index),
            };

        case FEE_ACTIONS.UPDATE_FEE:
            return {
                ...state,
                fees: state.fees.map((fee, index) =>
                    index === action.index ? action.payload : fee
                ),
            };

        case FEE_ACTIONS.ADD_TABLE_ROW:
            const feeToUpdate = state.fees[action.index];

            if (feeToUpdate.type === 'HourlyRate' && isHourlyRateRow(action.payload)) {
                const newRow = { ...action.payload, id: uuidv4() };
                return {
                    ...state,
                    fees: state.fees.map((fee, idx) => {
                        if (idx === action.index && fee.type === 'HourlyRate') {
                            return { ...fee, table: [...fee.table, newRow] } as HourlyRate;
                        }
                        return fee;
                    }),
                };
            } else if (feeToUpdate.type === 'LumpSum' && isLumpSumRow(action.payload)) {
                const newRow = { ...action.payload, id: uuidv4() };
                return {
                    ...state,
                    fees: state.fees.map((fee, idx) => {
                        if (idx === action.index && fee.type === 'LumpSum') {
                            return { ...fee, table: [...fee.table, newRow] } as LumpSum;
                        }
                        return fee;
                    }),
                };
            }
            return state;

        case FEE_ACTIONS.REMOVE_TABLE_ROW:
            const feeToRemoveFrom = state.fees[action.index];

            if (feeToRemoveFrom.type === 'HourlyRate') {
                return {
                    ...state,
                    fees: state.fees.map((fee, idx) => {
                        if (idx === action.index && fee.type === 'HourlyRate') {
                            return { ...fee, table: fee.table.filter((_, i) => i !== action.tableIndex) } as HourlyRate;
                        }
                        return fee;
                    }),
                };
            } else if (feeToRemoveFrom.type === 'LumpSum') {
                return {
                    ...state,
                    fees: state.fees.map((fee, idx) => {
                        if (idx === action.index && fee.type === 'LumpSum') {
                            return { ...fee, table: fee.table.filter((_, i) => i !== action.tableIndex) } as LumpSum;
                        }
                        return fee;
                    }),
                };
            }
            return state;

        case FEE_ACTIONS.UPDATE_TABLE_ROW:
            const feeToUpdateRow = state.fees[action.index];

            if (feeToUpdateRow.type === 'HourlyRate' && isHourlyRateRow(action.payload)) {
                return {
                    ...state,
                    fees: state.fees.map((fee, idx) => {
                        if (idx === action.index && fee.type === 'HourlyRate') {
                            return {
                                ...fee,
                                table: fee.table.map((row, i) =>
                                    i === action.tableIndex ? action.payload : row
                                ),
                            } as HourlyRate;
                        }
                        return fee;
                    }),
                };
            } else if (feeToUpdateRow.type === 'LumpSum' && isLumpSumRow(action.payload)) {
                return {
                    ...state,
                    fees: state.fees.map((fee, idx) => {
                        if (idx === action.index && fee.type === 'LumpSum') {
                            return {
                                ...fee,
                                table: fee.table.map((row, i) =>
                                    i === action.tableIndex ? action.payload : row
                                ),
                            } as LumpSum;
                        }
                        return fee;
                    }),
                };
            }
            return state;

        case FEE_ACTIONS.REORDER_FEES:
            return {
                ...state,
                fees: action.payload,
            };

        case FEE_ACTIONS.REORDER_TABLE_ROWS:
            return {
                ...state,
                fees: state.fees.map((fee, idx) => {
                    if (idx === action.index && ['LumpSum', 'HourlyRate'].includes(fee.type)) {
                        return { ...fee, table: action.payload } as LumpSum;
                    }
                    return fee;
                }),
            };

        default:
            return state;
    }
}

// Type guards
function isHourlyRateRow(value: any): value is HourlyRateRow {
    return value && typeof value === 'object' && 'id' in value && 'position' in value && 'rate' in value;
}

function isLumpSumRow(value: any): value is LumpSumRow {
    return value && typeof value === 'object' && 'id' in value && 'description' in value && 'fee' in value;
}